import * as THREE from 'three';

/**
 * Elevated isometric-style chase camera. Fixed diagonal offset gives the
 * signature angled read of the highway; x follows the bike with parallax lag
 * so lane changes sweep the composition without breaking control mapping.
 */
export class CameraRig {
  private readonly basePosition = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private followX = 0;
  private baseFov: number;
  private fovPunch = 0;

  /** Diagonal offset: right, up, back — isometric-leaning chase view. */
  private readonly offset = new THREE.Vector3(3.1, 7.6, 8.6);

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    this.baseFov = camera.fov;
  }

  setBaseFov(fov: number): void {
    this.baseFov = fov;
  }

  punchFov(degrees: number): void {
    this.fovPunch = Math.min(10, this.fovPunch + degrees);
  }

  snapTo(playerX: number): void {
    this.followX = playerX * 0.45;
    this.apply(0);
  }

  /**
   * @param delta REAL render delta (camera must stay live during hitstop)
   * @param speedRatio widens FOV slightly at speed
   */
  update(delta: number, playerX: number, lag: number, speedRatio: number): void {
    const factor = 1 - Math.exp(-delta / Math.max(0.001, lag));
    this.followX += (playerX * 0.45 - this.followX) * factor;

    if (this.fovPunch > 0.001) {
      this.fovPunch *= Math.exp(-delta / 0.2);
      if (this.fovPunch < 0.001) this.fovPunch = 0;
    }
    const speedFov = speedRatio * 6;
    this.camera.fov = this.baseFov + speedFov + this.fovPunch;
    this.camera.updateProjectionMatrix();
    this.apply(playerX);
  }

  private apply(playerX: number): void {
    this.basePosition.set(this.followX + this.offset.x, this.offset.y, this.offset.z);
    this.camera.position.copy(this.basePosition);
    // Look ahead down the track, slightly toward the player's lane.
    this.lookTarget.set(this.followX * 0.8 + playerX * 0.2, 0.4, -7.5);
    this.camera.lookAt(this.lookTarget);
  }
}
